import supabase from './supabase';
import emailNotificationService from './emailNotificationService';
import cacheService from '../services/cacheService.js';

// Check if we're in development mode
const isDevelopment = import.meta.env.MODE === 'development' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.includes('192.168.');

class NotificationService {
  constructor() {
    this.supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    this.emailService = emailNotificationService;
  }

  // Get user's IP address and user agent for security logging
  async getClientInfo() {
    try {
      // Skip external API call in development to prevent potential CORS issues
      if (isDevelopment) {
        return {
          ipAddress: '127.0.0.1',
          userAgent: navigator?.userAgent || 'Unknown',
          timestamp: new Date().toISOString(),
          environment: 'development'
        };
      }

      // IP detection is handled server-side by the Edge Function via x-forwarded-for headers
      // to comply with Content Security Policy restrictions
      return {
        ipAddress: 'Unknown',
        userAgent: navigator?.userAgent || 'Unknown',
        timestamp: new Date().toISOString()
      };
    } catch {
      return {
        ipAddress: 'Unknown',
        userAgent: navigator?.userAgent || 'Unknown',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Send authentication notification via Supabase Edge Function
  async sendAuthNotification(email, type, additionalMetadata = {}) {
    try {
      if (!email || !type) {
        return { success: false, error: 'Email and type are required' };
      }

      // Skip actual API call in development to prevent CORS errors
      if (isDevelopment) {
        return { 
          success: true, 
          data: { message: 'Notification skipped in development mode' },
          message: 'Development mode - notification simulated'
        };
      }

      const clientInfo = await this.getClientInfo();
      
      // Sanitize metadata to remove sensitive fields
      const sanitizedMetadata = this.sanitizeNotificationMetadata(additionalMetadata);
      
      const metadata = {
        ...clientInfo,
        ...sanitizedMetadata
      };

      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;

      const response = await fetch(`${this.supabaseUrl}/functions/v1/send-auth-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          email,
          type,
          metadata
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        return { 
          success: false, 
          error: result?.error || 'Failed to send notification'
        };
      }

      return { 
        success: true, 
        data: result,
        message: 'Notification sent successfully'
      };
    } catch {
      return { 
        success: false, 
        error: 'Failed to send notification - service unavailable'
      };
    }
  }

  // Sanitize notification metadata to prevent leaking sensitive information
  sanitizeNotificationMetadata(metadata = {}) {
    // Handle non-object types
    if (!metadata || typeof metadata !== 'object') {
      return metadata;
    }
    
    // Preserve arrays
    if (Array.isArray(metadata)) {
      return metadata.map(item => this.sanitizeNotificationMetadata(item));
    }
    
    const sensitiveFields = [
      'password', 
      'newPassword', 
      'oldPassword', 
      'currentPassword',
      'confirmPassword',
      'token',
      'accessToken',
      'refreshToken',
      'apiKey',
      'secret',
      'privateKey'
    ];

    const sanitized = { ...metadata };

    // Remove sensitive fields
    sensitiveFields.forEach(field => {
      delete sanitized[field];
    });

    // Recursively sanitize nested objects
    Object.keys(sanitized).forEach(key => {
      if (sanitized[key] && typeof sanitized[key] === 'object') {
        sanitized[key] = this.sanitizeNotificationMetadata(sanitized[key]);
      }
    });

    return sanitized;
  }

  // Convenience methods for specific notification types
  async notifyLoginSuccess(email, additionalData = {}) {
    return await this.sendAuthNotification(email, 'login_success', additionalData);
  }

  async notifyLoginFailure(email, reason, additionalData = {}) {
    return await this.sendAuthNotification(email, 'login_failure', {
      reason,
      ...additionalData
    });
  }

  async notifyPasswordResetRequest(email, additionalData = {}) {
    return await this.sendAuthNotification(email, 'password_reset_request', additionalData);
  }

  async notifySignupSuccess(email, role, additionalData = {}) {
    return await this.sendAuthNotification(email, 'signup_success', {
      role,
      ...additionalData
    });
  }

  // NEW METHODS FOR NOTIFICATION CRUD OPERATIONS

  // Get user's notification preferences
  async getNotificationPreferences(userId) {
    try {
      // Try cache first
      const cachedPrefs = await cacheService.getNotificationPreferences(userId);
      if (cachedPrefs) {
        return {
          success: true,
          preferences: cachedPrefs.preferences,
          role: cachedPrefs.role,
          fromCache: true
        };
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('notification_settings, role')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;

      const settings = data?.notification_settings?.email || {};
      
      // Cache the preferences (5min TTL)
      await cacheService.setNotificationPreferences(userId, { preferences: settings, role: data?.role });

      return {
        success: true,
        preferences: settings,
        role: data?.role,
        fromCache: false
      };
    } catch (error) {
      console.error('[NotificationService] Error fetching preferences:', error);
      return { success: false, error: error.message };
    }
  }

  // Update user's notification preferences
  async updateNotificationPreferences(userId, preferences) {
    try {
      // Get current settings to preserve other fields
      const { data: currentProfile, error: fetchError } = await supabase
        .from('user_profiles')
        .select('notification_settings')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;

      const updatedSettings = {
        ...currentProfile.notification_settings,
        email: preferences
      };

      const { error } = await supabase
        .from('user_profiles')
        .update({
          notification_settings: updatedSettings
        })
        .eq('id', userId);

      if (error) throw error;

      // Invalidate cache
      await cacheService.invalidateNotificationPreferences(userId);

      return { success: true };
    } catch (error) {
      console.error('[NotificationService] Error updating preferences:', error);
      return { success: false, error: error.message };
    }
  }

  // Check if user has notification enabled for specific type
  async isNotificationEnabled(userId, notificationType) {
    try {
      const { success, preferences } = await this.getNotificationPreferences(userId);
      
      if (!success) return false;
      
      return preferences[notificationType] === true;
    } catch (error) {
      console.error('[NotificationService] Error checking notification preference:', error);
      return false;
    }
  }

  // Get all notifications for a user
  async getUserNotifications(userId) {
    try {
      // Try cache first
      const cachedNotifications = await cacheService.getNotifications(userId);
      if (cachedNotifications) {
        return { success: true, data: cachedNotifications, fromCache: true };
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('id, user_id, type, title, message, data, is_read, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Cache the notifications (3min TTL)
      await cacheService.cacheNotifications(userId, data, 180);

      return { success: true, data, fromCache: false };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { success: false, error: error.message };
    }
  }

  // Get unread notifications count
  async getUnreadCount(userId) {
    try {
      // Try cache first
      const cachedCount = await cacheService.getNotificationCount(userId);
      if (cachedCount !== null) {
        return { success: true, count: cachedCount, fromCache: true };
      }

      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;

      // Cache the count
      await cacheService.setNotificationCount(userId, count || 0);

      return { success: true, count, fromCache: false };
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return { success: false, error: error.message };
    }
  }

  // Create a new notification for a user
  async createNotification(notification) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert(notification)
        .select()
        .single();

      if (error) throw error;

      // Invalidate caches and increment count
      await Promise.all([
        cacheService.invalidateNotifications(notification.user_id),
        cacheService.incrementNotificationCount(notification.user_id)
      ]);

      return { success: true, data };
    } catch (error) {
      console.error('Error creating notification:', error);
      return { success: false, error: error.message };
    }
  }

  // Enhanced notification sending with preferences check
  async sendNotification(userId, notificationType, emailData) {
    try {
      // Check if notifications are enabled for this type
      const isEnabled = await this.isNotificationEnabled(userId, notificationType);
      
      if (!isEnabled) {
        return { success: true, message: 'Notification disabled by user' };
      }

      // Get user profile for email
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('email, full_name')
        .eq('id', userId)
        .single();

      if (profileError) throw profileError;

      // Create in-app notification record
      await this.createInAppNotification(userId, notificationType, emailData);

      // Send email notification (simulate for now)
      const emailResult = await this.sendEmailNotification(
        userProfile.email,
        userProfile.full_name,
        notificationType,
        emailData
      );

      return emailResult;
    } catch (error) {
      console.error('[NotificationService] Error sending notification:', error);
      return { success: false, error: error.message };
    }
  }

  // Create in-app notification record
  async createInAppNotification(userId, type, data) {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title: data.subject || this.getDefaultSubject(type),
          message: data.message || this.getDefaultMessage(type, data),
          type: type,
          data: data
        });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('[NotificationService] Error creating in-app notification:', error);
      return { success: false, error: error.message };
    }
  }

  // Production-ready email notification with comprehensive delivery tracking
  async sendEmailNotification(email, fullName, type, data, options = {}) {
    const startTime = Date.now();
    let deliveryLogId = null;

    try {
      // In development, still log for debugging but also send real emails
      if (isDevelopment) {
        console.log(`📧 [EMAIL] Sending ${type} notification to ${email}`);
        console.log(`📧 To: ${fullName} <${email}>`);
        console.log(`📧 Content:`, data);
      }

      // Prepare email data based on notification type
      const emailPayload = this.prepareEmailData(type, email, fullName, data);
      
      if (!emailPayload) {
        console.warn(`[NotificationService] No email template for type: ${type}`);
        return { success: false, error: 'Unsupported notification type' };
      }

      // Create email delivery log entry
      const { data: logEntry, error: logError } = await supabase
        .from('email_delivery_logs')
        .insert({
          user_id: options.userId || null,
          email_address: email,
          email_type: type,
          subject: this.getEmailSubject(type, data),
          status: 'pending',
          metadata: {
            payload: emailPayload,
            recipient: fullName,
            startTime: new Date().toISOString()
          }
        })
        .select('id')
        .single();

      if (!logError && logEntry) {
        deliveryLogId = logEntry.id;
      }

      // Add delivery tracking to payload
      const enhancedPayload = {
        ...emailPayload,
        trackingId: deliveryLogId,
        metadata: {
          ...emailPayload.metadata,
          deliveryLogId,
          timestamp: new Date().toISOString()
        }
      };

      // Call the Supabase Edge Function with retry logic
      let attempt = 1;
      let lastError = null;
      const maxAttempts = options.maxRetries || 3;

      while (attempt <= maxAttempts) {
        try {
          const { data: result, error } = await supabase.functions.invoke('send-email', {
            body: enhancedPayload
          });

          if (error) {
            lastError = error;
            throw new Error(error.message);
          }

          if (!result?.success) {
            lastError = new Error(result?.error || 'Email sending failed');
            throw lastError;
          }

          // Success - update delivery log
          if (deliveryLogId) {
            await supabase
              .from('email_delivery_logs')
              .update({
                status: 'sent',
                provider_message_id: result.messageId || result.id,
                provider_status: 'accepted',
                sent_at: new Date().toISOString(),
                metadata: {
                  ...enhancedPayload.metadata,
                  deliveryTime: Date.now() - startTime,
                  attempt,
                  providerResponse: result
                }
              })
              .eq('id', deliveryLogId);
          }

          // Track metrics
          await this.trackEmailMetrics(email, type, 'sent', {
            deliveryTime: Date.now() - startTime,
            attempt,
            messageId: result.messageId
          });

          return { 
            success: true, 
            message: `Email notification sent to ${email}`,
            messageId: result.messageId || result.id,
            deliveryLogId,
            deliveryTime: Date.now() - startTime
          };

        } catch (attemptError) {
          lastError = attemptError;
          
          if (attempt < maxAttempts) {
            // Wait before retry with exponential backoff
            const delay = Math.pow(2, attempt) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
            
            console.log(`[NotificationService] Email attempt ${attempt} failed, retrying in ${delay}ms:`, attemptError.message);
            attempt++;
          } else {
            break;
          }
        }
      }

      // All attempts failed - update delivery log
      if (deliveryLogId) {
        await supabase
          .from('email_delivery_logs')
          .update({
            status: 'failed',
            retry_count: maxAttempts,
            metadata: {
              ...enhancedPayload.metadata,
              error: lastError?.message,
              totalTime: Date.now() - startTime,
              attempts: maxAttempts
            }
          })
          .eq('id', deliveryLogId);
      }

      // Track failure metrics
      await this.trackEmailMetrics(email, type, 'failed', {
        error: lastError?.message,
        attempts: maxAttempts,
        totalTime: Date.now() - startTime
      });

      console.error('[NotificationService] All email attempts failed:', lastError);
      return { 
        success: false, 
        error: lastError?.message || 'Email sending failed after all retry attempts',
        attempts: maxAttempts,
        deliveryLogId
      };

    } catch (error) {
      // Update delivery log with error
      if (deliveryLogId) {
        await supabase
          .from('email_delivery_logs')
          .update({
            status: 'error',
            metadata: {
              error: error.message,
              totalTime: Date.now() - startTime
            }
          })
          .eq('id', deliveryLogId);
      }

      console.error('[NotificationService] Critical error sending email:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper method to generate email subject based on type and data
  getEmailSubject(type, data) {
    const subjects = {
      'jobAlerts': `🎯 New Job Match: ${data.jobTitle || 'Perfect Opportunity'} ${data.company ? `at ${data.company}` : ''}`,
      'applications': `📋 Application Update: ${data.jobTitle || 'Your Application'} - ${data.status || 'Status Changed'}`,
      'newApplications': `📬 New Application: ${data.applicantName || 'Someone'} applied for ${data.jobTitle || 'your position'}`,
      'profileViews': `👀 Profile View: ${data.viewerCompany || 'An employer'} viewed your profile`,
      'messages': `💬 New message from ${data.senderName || 'someone'}`,
      'candidateMatches': `🎯 Candidate Match: ${data.candidateName || 'Perfect candidate'} for ${data.jobTitle || 'your position'}`,
      'jobPostings': `📢 Job Update: ${data.jobTitle || 'Your job posting'} - ${data.updateType || 'Updated'}`,
      'interviews': `🗓️ Interview ${data.interviewDate ? `scheduled for ${data.interviewDate}` : 'notification'}: ${data.jobTitle || 'Position'}`,
      'contractUpdates': `📋 Contract Update: ${data.projectName || data.contractTitle || 'Project'} - ${data.status || 'Updated'}`,
      'teamManagement': `👥 Team Invitation: ${data.teamName || data.companyName || 'Join our team'}`,
      'skillAssessments': `🎯 Skills Assessment: ${data.skillName || data.assessmentName || 'New assessment available'}`,
      'general': data.title || 'VA Sprouts Notification',
      'marketing': data.title || 'VA Sprouts Weekly Update',
      'digest': `📰 Your ${data.frequency || 'daily'} digest - ${data.totalCount || 0} new notifications`,
      'identity_verified': `✅ Identity Verified — Your VA Sprouts account is now verified`,
      'identity_rejected': `❌ Identity Verification Rejected — Action required`
    };

    return subjects[type] || `VA Sprouts Notification: ${type}`;
  }

  // Track email metrics for analytics and monitoring
  async trackEmailMetrics(email, type, status, metadata = {}) {
    try {
      await supabase.from('notification_metrics').insert({
        user_id: metadata.userId || null,
        notification_type: type,
        category: 'email',
        status,
        metadata: {
          email_address: email,
          delivery_time: metadata.deliveryTime,
          attempt: metadata.attempt,
          message_id: metadata.messageId,
          error: metadata.error,
          timestamp: new Date().toISOString(),
          ...metadata
        }
      });
    } catch (error) {
      console.error('Error tracking email metrics:', error);
    }
  }

  // Enhanced email data preparation with comprehensive notification type support
  prepareEmailData(type, email, fullName, data) {
    const basePayload = { 
      email: email,
      fullName: fullName
    };

    switch (type) {
      case 'jobAlerts':
        return {
          emailType: 'job-alert',
          ...basePayload,
          jobTitle: data.jobTitle || '',
          company: data.company || '',
          location: data.location || '',
          salary: data.salary || '',
          jobDescription: data.jobDescription || '',
          jobUrl: data.jobUrl || 'https://app.vasprouts.com/job-search'
        };

      case 'applications':
      case 'applicationStatus':
        return {
          emailType: 'application-status',
          ...basePayload,
          jobTitle: data.jobTitle || '',
          company: data.company || '',
          status: data.status || 'updated',
          statusMessage: data.statusMessage || '',
          nextSteps: data.nextSteps || ''
        };

      case 'newApplications':
        return {
          emailType: 'new-application',
          ...basePayload,
          jobTitle: data.jobTitle || '',
          applicantName: data.applicantName || '',
          applicationDate: data.applicationDate || new Date().toLocaleDateString(),
          coverLetterPreview: data.coverLetterPreview || '',
          applicationUrl: data.applicationUrl || 'https://app.vasprouts.com/employer-dashboard'
        };

      case 'profileViews':
        return {
          emailType: 'profile-view',
          ...basePayload,
          viewerCompany: data.viewerCompany || '',
          viewDate: data.viewDate || new Date().toLocaleDateString(),
          profileUrl: data.profileUrl || 'https://app.vasprouts.com/va-profile'
        };

      case 'messages':
      case 'messageNotification':
        return {
          emailType: 'message-notification',
          ...basePayload,
          senderName: data.senderName || 'Someone',
          messagePreview: data.messagePreview || data.message || '',
          conversationUrl: data.conversationUrl || 'https://app.vasprouts.com/messages'
        };

      case 'candidateMatches':
        return {
          emailType: 'candidate-match',
          ...basePayload,
          jobTitle: data.jobTitle || '',
          candidateName: data.candidateName || '',
          matchScore: data.matchScore || null,
          candidateProfileUrl: data.candidateProfileUrl || 'https://app.vasprouts.com/candidate-search'
        };

      case 'jobPostings':
      case 'jobPostingUpdate':
        return {
          emailType: 'job-posting-update',
          ...basePayload,
          jobTitle: data.jobTitle || '',
          updateType: data.updateType || 'updated',
          updateMessage: data.updateMessage || 'Your job posting has been updated.',
          jobUrl: data.jobUrl || 'https://app.vasprouts.com/employer-dashboard'
        };

      case 'interviews':
      case 'interviewScheduled':
        return {
          emailType: 'interview-scheduled',
          ...basePayload,
          jobTitle: data.jobTitle || '',
          employerName: data.employerName || data.candidateName || 'the employer',
          candidateName: data.candidateName || data.employerName || 'the candidate',
          interviewDate: data.interviewDate || new Date().toLocaleDateString(),
          interviewTime: data.interviewTime || 'TBD',
          interviewType: data.interviewType || 'Video Call',
          interviewDuration: data.interviewDuration || '60',
          interviewTimezone: data.interviewTimezone || 'EST',
          meetingLink: data.meetingLink || '',
          interviewNotes: data.interviewNotes || ''
        };

      case 'contractUpdates':
      case 'contractStatus':
        return {
          emailType: 'job-posting-update', // Reuse template with contract context
          ...basePayload,
          jobTitle: `Contract: ${data.projectName || data.contractTitle || 'Project Update'}`,
          updateType: data.status || 'updated',
          updateMessage: data.updateMessage || 'Your contract has been updated.',
          jobUrl: data.contractUrl || 'https://app.vasprouts.com/contracts'
        };

      case 'teamManagement':
      case 'teamInvitation':
        return {
          emailType: 'job-posting-update', // Reuse template with team context
          ...basePayload,
          jobTitle: `Team Invitation: ${data.teamName || data.companyName || 'Join Our Team'}`,
          updateType: 'invitation',
          updateMessage: data.invitationMessage || 'You have been invited to join a team.',
          jobUrl: data.invitationUrl || 'https://app.vasprouts.com/team'
        };

      case 'skillAssessments':
      case 'assessmentInvitation':
        return {
          emailType: 'job-alert', // Reuse template with assessment context
          ...basePayload,
          jobTitle: `Skills Assessment: ${data.skillName || data.assessmentName || 'New Assessment Available'}`,
          company: 'VA Sprouts Skills Center',
          location: 'Online Assessment',
          salary: `Complete by: ${data.deadline || 'No deadline'}`,
          jobDescription: data.description || 'A new skills assessment is available for you to take.',
          jobUrl: data.assessmentUrl || 'https://app.vasprouts.com/assessments'
        };

      case 'identity_verified':
        return {
          emailType: 'job-posting-update',
          ...basePayload,
          jobTitle: '✅ Identity Verification Approved',
          updateType: 'verified',
          updateMessage: data.message || 'Congratulations! Your identity has been verified by our team. Your profile will now display a verification badge.',
          jobUrl: data.actionUrl || 'https://app.vasprouts.com/va-profile'
        };

      case 'identity_rejected':
        return {
          emailType: 'job-posting-update',
          ...basePayload,
          jobTitle: '❌ Identity Verification Not Approved',
          updateType: 'rejected',
          updateMessage: data.message || `Your identity verification was not approved.${ data.notes ? ` Reason: ${data.notes}` : '' } Please re-submit with a valid QR code image link.`,
          jobUrl: data.actionUrl || 'https://app.vasprouts.com/va-profile'
        };

      case 'general':
      case 'system':
      case 'security':
        return {
          emailType: 'job-posting-update', // Reuse template for system messages
          ...basePayload,
          jobTitle: data.title || 'System Notification',
          updateType: data.type || 'notification',
          updateMessage: data.message || 'You have a new system notification.',
          jobUrl: data.actionUrl || 'https://app.vasprouts.com/dashboard'
        };

      case 'marketing':
      case 'newsletter':
      case 'tips':
        return {
          emailType: 'job-alert', // Reuse template for marketing content
          ...basePayload,
          jobTitle: data.title || 'VA Sprouts Weekly Update',
          company: 'VA Sprouts Team',
          location: 'Platform News',
          salary: 'Free insights and tips',
          jobDescription: data.content || data.message || 'Check out the latest updates and insights.',
          jobUrl: data.readMoreUrl || 'https://app.vasprouts.com/blog'
        };

      default:
        console.warn(`[NotificationService] No email template for notification type: ${type}`);
        return null;
    }
  }

  // Get default subject for notification type
  getDefaultSubject(type) {
    const subjects = {
      'general': 'VA Sprouts Notification',
      'jobAlerts': 'New Job Match Found!',
      'applications': 'Application Status Update',
      'interviews': 'Interview Notification',
      'profileViews': 'Your Profile Was Viewed',
      'messages': 'New Message Received',
      'newApplications': 'New Job Application Received',
      'candidateMatches': 'Candidate Match Found',
      'jobPostings': 'Job Posting Update',
      'marketing': 'VA Sprouts Updates',
      'identity_verified': 'Identity Verification Approved',
      'identity_rejected': 'Identity Verification Not Approved'
    };

    return subjects[type] || 'VA Sprouts Notification';
  }

  // Get default message for notification type
  getDefaultMessage(type, data) {
    const messages = {
      'general': 'You have a new notification from VA Sprouts.',
      'jobAlerts': `A new job "${data.jobTitle || 'opportunity'}" matches your skills and preferences.`,
      'applications': `Your application status has been updated to "${data.status || 'under review'}".`,
      'interviews': `You have an interview scheduled for "${data.jobTitle || 'a position'}".`,
      'profileViews': `An employer has viewed your profile.`,
      'messages': `You have received a new message from ${data.senderName || 'another user'}.`,
      'newApplications': `You have received a new application for "${data.jobTitle || 'your job posting'}".`,
      'candidateMatches': `A candidate matching your requirements has been found for "${data.jobTitle || 'your job posting'}".`,
      'jobPostings': `Your job posting "${data.jobTitle || 'has been updated'}" status has changed.`,
      'marketing': 'Check out the latest updates and tips from VA Sprouts.',
      'identity_verified': 'Congratulations! Your identity has been verified. A verification badge will now appear on your profile.',
      'identity_rejected': `Your identity verification was not approved.${data.notes ? ` Reason: ${data.notes}` : ' Please re-submit with a valid QR code image link.'}`
    };

    return messages[type] || 'You have a new notification from VA Sprouts.';
  }



  // Mark a notification as read
  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)
        .select()
        .single();

      if (error) throw error;

      // Decrement count and invalidate caches
      await Promise.all([
        cacheService.decrementNotificationCount(data.user_id),
        cacheService.invalidateNotifications(data.user_id)
      ]);

      return { success: true, data };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { success: false, error: error.message };
    }
  }

  // Mark all notifications as read for a user
  async markAllAsRead(userId) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;

      // Reset count and invalidate caches
      await Promise.all([
        cacheService.resetNotificationCount(userId),
        cacheService.invalidateNotifications(userId)
      ]);

      return { success: true };
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete a notification
  async deleteNotification(notificationId) {
    try {
      // Get the notification first to know the user_id
      const { data: notification, error: fetchError } = await supabase
        .from('notifications')
        .select('user_id, is_read')
        .eq('id', notificationId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      // If notification was unread, decrement count
      if (!notification.is_read) {
        await cacheService.decrementNotificationCount(notification.user_id);
      }

      // Invalidate cache
      await cacheService.invalidateNotifications(notification.user_id);

      return { success: true };
    } catch (error) {
      console.error('Error deleting notification:', error);
      return { success: false, error: error.message };
    }
  }

  // Production-ready batch notification system with retry logic and rate limiting
  async sendBatchNotifications(notifications, options = {}) {
    const { 
      maxConcurrency = 3,
      retryAttempts = 2,
      retryDelay = 1000,
      progressCallback = null 
    } = options;
    
    const results = [];
    const batches = this.chunkArray(notifications, maxConcurrency);
    let processed = 0;

    for (const batch of batches) {
      const batchPromises = batch.map(async (notification) => {
        let attempts = 0;
        let result = null;
        
        while (attempts <= retryAttempts) {
          try {
            if (notification.type === 'auth') {
              result = await this.sendAuthNotification(
                notification.email,
                notification.subType || notification.type,
                notification.metadata
              );
            } else {
              result = await this.sendNotification(
                notification.userId,
                notification.type,
                notification.data
              );
            }
            
            if (result.success) {
              break; // Success, exit retry loop
            }
            
            attempts++;
            if (attempts <= retryAttempts) {
              await new Promise(resolve => setTimeout(resolve, retryDelay * attempts));
            }
          } catch (error) {
            attempts++;
            result = { success: false, error: error.message };
            
            if (attempts <= retryAttempts) {
              await new Promise(resolve => setTimeout(resolve, retryDelay * attempts));
            }
          }
        }

        processed++;
        if (progressCallback) {
          progressCallback(processed, notifications.length);
        }

        return {
          ...notification,
          result,
          attempts
        };
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
      
      // Rate limiting between batches
      if (batches.indexOf(batch) < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    return {
      results,
      summary: {
        total: notifications.length,
        successful: results.filter(r => r.result?.success).length,
        failed: results.filter(r => !r.result?.success).length
      }
    };
  }

  // Utility function to chunk arrays
  chunkArray(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  // Enhanced notification for VAs with comprehensive error handling
  async notifyVA(vaId, type, data) {
    try {
      // Validate input
      if (!vaId || !type) {
        return { success: false, error: 'Missing required parameters: vaId and type' };
      }

      // Get user profile and check notification preferences
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('email, full_name, notification_settings')
        .eq('id', vaId)
        .eq('role', 'va')
        .single();

      if (profileError) {
        console.error('[NotificationService] Error fetching VA profile:', profileError);
        return { success: false, error: 'User profile not found' };
      }

      // Check if this notification type is enabled
      const notificationSettings = userProfile.notification_settings || {};
      const emailSettings = notificationSettings.email || {};
      
      if (!emailSettings[type] && type !== 'general' && type !== 'security') {
        console.log(`[NotificationService] Notification type '${type}' disabled for user ${vaId}`);
        return { success: true, message: 'Notification disabled by user preference' };
      }

      // Create in-app notification
      await this.createInAppNotification(vaId, type, data);

      // Send email notification if enabled
      const emailResult = await this.sendEmailNotification(
        userProfile.email,
        userProfile.full_name,
        type,
        data
      );

      return emailResult;
    } catch (error) {
      console.error('[NotificationService] Error in notifyVA:', error);
      return { success: false, error: error.message };
    }
  }

  // Enhanced notification for Employers with comprehensive error handling
  async notifyEmployer(employerId, type, data) {
    try {
      // Validate input
      if (!employerId || !type) {
        return { success: false, error: 'Missing required parameters: employerId and type' };
      }

      // Get user profile and check notification preferences
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('email, full_name, notification_settings')
        .eq('id', employerId)
        .eq('role', 'employer')
        .single();

      if (profileError) {
        console.error('[NotificationService] Error fetching employer profile:', profileError);
        return { success: false, error: 'User profile not found' };
      }

      // Check if this notification type is enabled
      const notificationSettings = userProfile.notification_settings || {};
      const emailSettings = notificationSettings.email || {};
      
      if (!emailSettings[type] && type !== 'general' && type !== 'security') {
        console.log(`[NotificationService] Notification type '${type}' disabled for employer ${employerId}`);
        return { success: true, message: 'Notification disabled by user preference' };
      }

      // Create in-app notification
      await this.createInAppNotification(employerId, type, data);

      // Send email notification if enabled
      const emailResult = await this.sendEmailNotification(
        userProfile.email,
        userProfile.full_name,
        type,
        data
      );

      return emailResult;
    } catch (error) {
      console.error('[NotificationService] Error in notifyEmployer:', error);
      return { success: false, error: error.message };
    }
  }

  // Smart digest email system for batching notifications
  async sendDigestEmail(userId, notifications, frequency = 'daily') {
    try {
      if (!notifications || notifications.length === 0) {
        return { success: true, message: 'No notifications to send' };
      }

      // Get user profile
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('email, full_name, role')
        .eq('id', userId)
        .single();

      if (profileError) {
        return { success: false, error: 'User profile not found' };
      }

      // Group notifications by type
      const groupedNotifications = notifications.reduce((acc, notification) => {
        const type = notification.type || 'general';
        if (!acc[type]) acc[type] = [];
        acc[type].push(notification);
        return acc;
      }, {});

      // Create digest email data
      const digestData = {
        userId,
        userRole: userProfile.role,
        frequency,
        notificationGroups: groupedNotifications,
        totalCount: notifications.length,
        summaryDate: new Date().toLocaleDateString(),
        digestUrl: 'https://app.vasprouts.com/notifications'
      };

      // Send digest email (reuse job-alert template for digest format)
      const emailResult = await this.sendEmailNotification(
        userProfile.email,
        userProfile.full_name,
        'digest',
        digestData
      );

      if (emailResult.success) {
        // Mark notifications as sent
        await supabase
          .from('notifications')
          .update({ 
            is_read: false, // Keep unread but mark as emailed
            emailed_at: new Date().toISOString()
          })
          .in('id', notifications.map(n => n.id));
      }

      return emailResult;
    } catch (error) {
      console.error('[NotificationService] Error sending digest email:', error);
      return { success: false, error: error.message };
    }
  }

  // Email delivery monitoring and retry system
  async monitorEmailDelivery(emailId, maxRetries = 3) {
    let attempts = 0;
    
    while (attempts < maxRetries) {
      try {
        // This would integrate with Resend's webhook system or delivery API
        // For now, we simulate successful delivery after first attempt
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        console.log(`[NotificationService] Email ${emailId} delivered successfully`);
        return { success: true, delivered: true, attempts: attempts + 1 };
      } catch (error) {
        attempts++;
        console.error(`[NotificationService] Email delivery attempt ${attempts} failed:`, error);
        
        if (attempts >= maxRetries) {
          return { success: false, delivered: false, attempts, error: error.message };
        }
        
        // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempts) * 1000));
      }
    }
  }

  // Health check for the notification service
  async healthCheck() {
    try {
      // Skip actual API call in development to prevent CORS errors
      if (isDevelopment) {
        return {
          available: true,
          status: 200,
          timestamp: new Date().toISOString(),
          environment: 'development',
          message: 'Notification service simulated in development mode',
          functionExists: true // Simulate that the function exists in development
        };
      }

      // First check if the function exists using OPTIONS request
      const response = await fetch(`${this.supabaseUrl}/functions/v1/send-auth-notification`, {
        method: 'OPTIONS',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // If the function doesn't exist, provide a specific error message
      if (response.status === 404) {
        return {
          available: false,
          status: response.status,
          timestamp: new Date().toISOString(),
          message: 'Requested function was not found. The Supabase Edge Function "send-auth-notification" may not be deployed.',
          functionExists: false
        };
      }

      return {
        available: response.ok,
        status: response.status,
        timestamp: new Date().toISOString(),
        message: response.ok ? 'Notification service is available' : 'Notification service is unavailable',
        functionExists: true
      };
    } catch (error) {
      return {
        available: false,
        status: 500,
        error: error.message,
        timestamp: new Date().toISOString(),
        message: 'Failed to connect to notification service - network error',
        functionExists: false
      };
    }
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;